import * as THREE from 'three';
import { hexToWorld, type BattleState, type DungeonLayout, type MapDef } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import type { CryptBuild } from './crypt.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';

/**
 * The titan's neck (El Párpado 1, docs/SAGAS.md): the crypt re-dressed as a gorge of the titan's own skin. The slate
 * goes to old grey-green hide, the walls to its heavy ridges; creases run across the floor the way a turtle's neck
 * wrinkles, with pores, pale old scars and horn scutes on the ridges. Along the walls the skin swells: it bulges in on
 * the in-breath and sinks back on the out-breath (`s.neck.open`). The doors between rooms are folds: two great lips
 * of skin that roll shut across the way (`doorOpen`, as the crypt's bars would) and draw back into the walls; a red
 * seam glows on the floor when a fold is about to shut, and one squeezing a hero throws a spray where they are put back.
 *
 * The shake (`ShakeFx`, the Order's post uses it too): while the dust is up (`s.neck.warned`) it jumps off the skin
 * round every hero; then a jolt of the camera, a ring of dust, and a hero who was walking goes down with a thud.
 *
 * Draw calls: the dressing (one merged mesh), the swelling (one instanced), the folds (one instanced), a glow layer.
 * No lights of its own: the crypt's room lights are re-coloured to a pale daylight.
 */

const SKIN = [new THREE.Color('#6c7660'), new THREE.Color('#626d57'), new THREE.Color('#737d66')];
const SKIN_RIDGE = [new THREE.Color('#7f896f'), new THREE.Color('#758066')];
const SKIN_SIDE = new THREE.Color('#465040');
const SKIN_FOOT = new THREE.Color('#20251c');
const GROUT = new THREE.Color('#465039');
const CREASE = '#2c3226';
const CREASE_LIT = '#78826a';
const PORE = '#30362a';
const SCAR = '#a0a48e';
const SCUTE = ['#7c876b', '#737e63', '#838d71'];
const SCUTE_IN = ['#5d684f', '#566148'];
const FOLD = '#7f746a';
const SEAM = new THREE.Color('#ff4f3a');
const DUST = '#b8b4a2';

type Pt = { x: number; y: number };

/** Dust off the skin before the titan shakes, the jolt, and a hero going down: for the neck and the post alike. */
export class ShakeFx {
  private v = new THREE.Vector3();
  constructor(private ctx: OmenCtx) {}

  tick(s: BattleState): void {
    const st = s.neck;
    const low = this.ctx.low;
    // The dust jumping off the skin: round every hero standing, and a little everywhere near them.
    if (st?.warned && s.tick % (low ? 3 : 2) === 0) {
      for (const h of s.heroes) {
        if (h.downTicks > 0) continue;
        const p = this.ctx.pos(h.id) ?? this.ctx.at(h.x, h.y, 0);
        for (let k = 0; k < (low ? 2 : 4); k++) {
          const a = (s.tick * 1.7 + k * 2.4 + h.id) % (Math.PI * 2);
          const d = 0.4 + ((s.tick * 7 + k * 13 + h.id) % 23) / 10;
          this.v.set(p.x + Math.cos(a) * d, 0, p.z + Math.sin(a) * d);
          this.v.y = this.ctx.ground(this.v.x, this.v.z) + 0.03;
          this.ctx.vfx.burst(this.v, { n: 1, color: DUST, speed: 0.01, up: 0.06, size: 0.07, life: 14, gravity: 0.008, spread: 0.12 });
          if (k === 0) this.ctx.vfx.burst(this.v, { n: 1, color: '#b0ad9c', speed: 0.004, up: 0.012, size: 0.75, life: 24, smoke: true, spread: 0.25 });
        }
      }
    }
    for (const e of s._events ?? []) {
      if (e.t !== 'neck') continue;
      if (e.kind === 'shake') {
        this.ctx.shake(0.5);
        for (const h of s.heroes) {
          if (h.downTicks > 0) continue;
          const p = this.ctx.pos(h.id) ?? this.ctx.at(h.x, h.y, 0);
          this.v.copy(p).setY(this.ctx.ground(p.x, p.z) + 0.05);
          this.ctx.vfx.ring(this.v, 0.2, 2.2, DUST, 14, 0.7);
          this.ctx.vfx.burst(this.v, { n: low ? 4 : 10, color: '#a8a492', speed: 0.03, up: 0.03, size: 0.6, life: 30, smoke: true, spread: 1.2 });
        }
      } else if (e.kind === 'fall') {
        const p = this.ctx.at(e.x, e.y, 0.05);
        this.ctx.flare(p.clone().setY(p.y + 0.4), '#ff8a6e', 1.8, 10);
        this.ctx.vfx.ring(p, 0.1, 1.1, '#ff8a6e', 10, 0.8);
        this.ctx.vfx.burst(p, { n: low ? 3 : 8, color: DUST, speed: 0.04, up: 0.04, size: 0.4, life: 20, smoke: true, spread: 0.5 });
      }
    }
  }
}

export class NeckLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(160);
  private fx: ShakeFx;
  /** The skin swelling along the walls: one instance each (rest pose kept to breathe from). */
  private swell: THREE.InstancedMesh;
  private swellRest: { p: THREE.Vector3; yaw: number; s: THREE.Vector3 }[] = [];
  /** The folds: two lips a door, instanced; each door's way across and how shut it is (0 shut … 1 open). */
  private lips: THREE.InstancedMesh;
  private folds: { door: CryptBuild['doors'][number]; c: THREE.Vector3; ax: number; az: number; yaw: number; open: number }[] = [];
  private breath = 1;
  private now = 0;
  private last = 0;
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private e = new THREE.Euler();
  private sv = new THREE.Vector3();
  private pv = new THREE.Vector3();

  constructor(
    private ctx: OmenCtx,
    map: MapDef,
    private crypt: CryptBuild,
    rooms: THREE.PointLight[],
  ) {
    this.group.name = 'neck';
    this.fx = new ShakeFx(ctx);
    const L = map.dungeon as DungeonLayout;
    const S = WORLD_PER_HEX;
    crypt.runes.visible = false;
    this.skin(crypt.mesh);
    // Daylight down the gorge, pale and a little green; no sconces on a titan's hide.
    rooms.forEach((l) => {
      l.color.set(0xdfe6cf);
      l.intensity = 18;
    });
    for (const f of crypt.flames) {
      f.mesh.visible = false;
      f.glow.visible = false;
    }
    // No sconces, candles or skulls on a titan's hide: its rubble, roots and moss stay.
    const gone = new Set(['2b2a2e', 'cfc3a4', 'd8d0bd', 'fff2b0']);
    crypt.decor.traverse((o) => {
      const m = (o as THREE.Mesh).material as THREE.MeshStandardMaterial | undefined;
      if ((o as THREE.Mesh).isMesh && m?.color && gone.has(m.color.getHexString())) o.visible = false;
    });
    const box = new THREE.BoxGeometry(1, 1, 1);
    const disc = new THREE.CircleGeometry(0.5, 10).rotateX(-Math.PI / 2);
    const plate = new THREE.CylinderGeometry(0.5, 0.56, 1, 6);
    const kit = new Kit();
    const floor = new Set(L.floor.map((h) => `${h.q},${h.r}`));
    const pts = L.rooms.map((r) => hexToWorld(r.q, r.r));
    // How high the crypt stands at each cell's centre (its prism's top), read off the mesh: the walls' tops.
    const tops = new Map<string, number>();
    {
      const pos = crypt.mesh.geometry.getAttribute('position') as THREE.BufferAttribute;
      for (let i = 0; i < pos.count; i++) {
        const k = `${Math.round(pos.getX(i) * 50)},${Math.round(pos.getZ(i) * 50)}`;
        const y = pos.getY(i);
        if (y > (tops.get(k) ?? -9)) tops.set(k, y);
      }
    }
    const topAt = (x: number, z: number): number => tops.get(`${Math.round(x * 50)},${Math.round(z * 50)}`) ?? 0;
    // The neck's way: room to room. Creases run across it.
    L.rooms.forEach((room, i) => {
      const w = pts[i] as Pt;
      const c = ctx.at(w.x, w.y, 0);
      const rw = room.radius * S;
      const nb = pts[i + 1] ?? pts[i - 1] ?? { x: w.x + 1000, y: w.y };
      const dir = Math.atan2(nb.x - w.x, nb.y - w.y);
      this.creases(kit, disc, c, rw, dir, i);
    });
    // Every ridge (wall) cell: a scute of the hide on its top, darker in the middle; where it faces the way, the
    // hide swells out over the floor at its foot.
    const seen = new Set<string>();
    let k = 0;
    for (const h of L.floor) {
      const a = hexToWorld(h.q, h.r);
      for (const [dq, dr] of [
        [1, 0],
        [1, -1],
        [0, -1],
        [-1, 0],
        [-1, 1],
        [0, 1],
      ] as const) {
        const key = `${h.q + dq},${h.r + dr}`;
        if (floor.has(key) || seen.has(key)) continue;
        seen.add(key);
        k++;
        const b = hexToWorld(h.q + dq, h.r + dr);
        const x = (b.x / 1000) * S;
        const z = (b.y / 1000) * S;
        const tx = (a.x / 1000) * S;
        const tz = (a.y / 1000) * S;
        const top = topAt(x, z);
        const yaw = Math.atan2(tx - x, tz - z);
        if (top > 0.3) {
          kit.add(plate, SCUTE[k % 3] as string, place(x, top + 0.02, z, Math.PI / 6, new THREE.Vector3(1.12, 0.05, 1.12)));
          kit.add(plate, SCUTE_IN[k % 2] as string, place(x, top + 0.05, z, Math.PI / 6 + 0.1 * Math.sin(k), new THREE.Vector3(0.7, 0.05, 0.7)));
          if (k % 4 === 0) kit.add(box, SCAR, place(x, top + 0.081, z, k, new THREE.Vector3(0.5, 0.008, 0.04)));
        }
        // The swelling: a soft bulge of hide out of the ridge's foot over the way.
        if (k % 2 === 0) {
          const t = 0.5 + 0.25 * Math.abs(Math.sin(k * 1.7));
          const mx = (x + tx) / 2;
          const mz = (z + tz) / 2;
          this.swellRest.push({ p: new THREE.Vector3(mx - Math.sin(yaw) * 0.08, 0, mz - Math.cos(yaw) * 0.08), yaw, s: new THREE.Vector3(0.62 + t * 0.3, 0.4 + t * 0.25, 0.42 + t * 0.15) });
        }
      }
    }
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.92, metalness: 0, flatShading: true });
    this.mats.push(mat);
    const dress = kit.build(mat, 'neck-dress');
    if (dress) {
      dress.receiveShadow = true;
      this.geos.push(dress.geometry);
      this.group.add(dress);
    }
    // The swelling hide: half-domes, instanced, breathing.
    const domeG = new THREE.SphereGeometry(0.5, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2);
    const domeM = new THREE.MeshStandardMaterial({ color: '#68735c', roughness: 0.88, metalness: 0, flatShading: true });
    this.geos.push(domeG);
    this.mats.push(domeM);
    this.swell = new THREE.InstancedMesh(domeG, domeM, Math.max(1, this.swellRest.length));
    this.swell.count = this.swellRest.length;
    this.swell.name = 'neck-swell';
    this.swell.receiveShadow = true;
    this.swell.frustumCulled = false;
    this.group.add(this.swell);
    // The folds: a lip of skin either side of every door, rolled across it to shut.
    for (const door of crypt.doors) {
      // The door's stone frame and bars go; the frame's turn is the way through (its across is local x).
      const frame = door.bars.parent;
      const yaw = frame?.rotation.y ?? 0;
      if (frame) frame.visible = false;
      this.folds.push({ door, c: door.pos.clone(), ax: Math.cos(yaw), az: -Math.sin(yaw), yaw, open: 1 });
    }
    const lipG = new THREE.CapsuleGeometry(0.5, 1, 4, 10);
    const lipM = new THREE.MeshStandardMaterial({ color: FOLD, roughness: 0.8, metalness: 0, flatShading: true });
    this.geos.push(lipG);
    this.mats.push(lipM);
    this.lips = new THREE.InstancedMesh(lipG, lipM, Math.max(1, this.folds.length * 2));
    this.lips.count = this.folds.length * 2;
    this.lips.name = 'neck-folds';
    this.lips.frustumCulled = false;
    this.group.add(this.lips);
    this.group.add(this.glow.mesh);
    for (const g of [box, disc, plate]) g.dispose();
    this.pose(0);
  }

  /** The crypt's slate and walls to the titan's hide: grey-green skin, its ridges paler, the grout its creases. */
  private skin(mesh: THREE.Mesh): void {
    const geo = mesh.geometry;
    const col = geo.getAttribute('color') as THREE.BufferAttribute | undefined;
    const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (!col || !pos) return;
    const c = new THREE.Color();
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const y = pos.getY(i);
      const z = pos.getZ(i);
      const lum = col.getX(i) * 0.3 + col.getY(i) * 0.59 + col.getZ(i) * 0.11;
      const n = 0.5 + 0.5 * Math.sin(x * 3.1 + z * 1.7) * Math.cos(z * 2.3 - x * 0.9);
      if (y > 0.5) c.copy(SKIN_RIDGE[n > 0.5 ? 0 : 1] as THREE.Color).multiplyScalar(0.92 + n * 0.12);
      else if (y > 0.04) c.copy(SKIN_SIDE).lerp(SKIN_FOOT, Math.max(0, 0.5 - y));
      else if (lum < 0.04) c.copy(GROUT);
      else c.copy(SKIN[Math.floor(n * 2.99)] as THREE.Color).multiplyScalar(0.9 + lum * 1.2);
      col.setXYZ(i, c.r, c.g, c.b);
    }
    col.needsUpdate = true;
  }

  /** Wrinkles across the way, in soft arcs, each with a lit lip beside it; pores; now and then a pale old scar. */
  private creases(kit: Kit, disc: THREE.BufferGeometry, c: THREE.Vector3, rw: number, dir: number, i: number): void {
    const fx = Math.sin(dir);
    const fz = Math.cos(dir);
    const ax = Math.cos(dir);
    const az = -Math.sin(dir);
    const g = c.y;
    for (let k = -3; k <= 3; k++) {
      const along = (k / 3.3) * rw * 0.85;
      const half = Math.sqrt(Math.max(0.1, rw * rw - along * along)) * 0.92;
      const bow = (0.18 + 0.06 * (k % 2)) * (k % 2 ? 1 : -1);
      const line: THREE.Vector3[] = [];
      for (let n = 0; n <= 8; n++) {
        const u = (n / 8) * 2 - 1;
        const off = u * half;
        const b = (1 - u * u) * bow + 0.04 * Math.sin(u * 7 + k);
        line.push(new THREE.Vector3(c.x + fx * (along + b) + ax * off, g + 0.01, c.z + fz * (along + b) + az * off));
      }
      const curve = new THREE.CatmullRomCurve3(line);
      const dark = new THREE.TubeGeometry(curve, 16, 0.035, 4, false).scale(1, 0.35, 1);
      kit.add(dark, CREASE, new THREE.Matrix4().makeTranslation(0, g * 0.65, 0));
      dark.dispose();
      // The fold's lip: a paler ridge just beside the crease.
      const lip = new THREE.TubeGeometry(curve, 16, 0.045, 4, false).scale(1, 0.4, 1);
      kit.add(lip, CREASE_LIT, new THREE.Matrix4().makeTranslation(fx * 0.075, g * 0.6 + 0.004, fz * 0.075));
      lip.dispose();
    }
    for (let k = 0; k < 14; k++) {
      const a = k * 2.39 + i;
      const d = rw * (0.15 + ((k * 7) % 11) / 13);
      kit.add(disc, PORE, place(c.x + Math.cos(a) * d, g + 0.016, c.z + Math.sin(a) * d, a, 0.05 + (k % 3) * 0.025));
    }
    if (i % 2 === 1) {
      const a = i * 1.3;
      const p: THREE.Vector3[] = [];
      for (let n = 0; n <= 5; n++) p.push(new THREE.Vector3(c.x + Math.cos(a) * rw * (0.2 + n * 0.09), g + 0.012, c.z + Math.sin(a) * rw * (0.2 + n * 0.09) + 0.08 * Math.sin(n * 1.7)));
      const scar = new THREE.TubeGeometry(new THREE.CatmullRomCurve3(p), 10, 0.04, 4, false).scale(1, 0.4, 1);
      kit.add(scar, SCAR, new THREE.Matrix4().makeTranslation(0, g * 0.6, 0));
      scar.dispose();
    }
  }

  /** The swelling and the folds as they stand now. */
  private pose(t: number): void {
    // In-breath (folds shut): the hide bulges in; out-breath: it sinks back, with a slow heave under it all.
    const heave = 1 + 0.12 * (1 - this.breath) + 0.03 * Math.sin(t * 1.3);
    this.swellRest.forEach((r, i) => {
      this.e.set(-0.25, r.yaw, 0);
      this.q.setFromEuler(this.e);
      this.sv.set(r.s.x * (1 + 0.04 * (1 - this.breath)), r.s.y * heave * (1 + 0.02 * Math.sin(t * 1.3 + i)), r.s.z * heave);
      this.m.compose(r.p, this.q, this.sv);
      this.swell.setMatrixAt(i, this.m);
    });
    this.swell.instanceMatrix.needsUpdate = true;
    const S = WORLD_PER_HEX;
    this.folds.forEach((f, i) => {
      const o = f.open;
      for (const side of [-1, 1]) {
        // Shut: the lips meet over the door, swollen; open: drawn back to the corridor's sides, slack.
        const d = (0.17 + o * 0.36) * S * side;
        const fat = 1.12 - o * 0.3;
        this.pv.set(f.c.x + f.ax * d, 0.36 + (1 - o) * 0.08, f.c.z + f.az * d);
        // Lying along the way (the capsule's length turned from up to the way through), leaning out as it opens.
        this.e.set(Math.PI / 2, f.yaw, side * o * 0.35, 'YXZ');
        this.q.setFromEuler(this.e);
        this.sv.set(0.62 * fat, 0.55, 0.95 * fat);
        this.m.compose(this.pv, this.q, this.sv);
        this.lips.setMatrixAt(i * 2 + (side > 0 ? 1 : 0), this.m);
      }
    });
    this.lips.instanceMatrix.needsUpdate = true;
  }

  /** Every fresh tick: the dust and the jolt; a squeeze's spray. */
  tick(s: BattleState): void {
    this.fx.tick(s);
    const low = this.ctx.low;
    for (const e of s._events ?? []) {
      if (e.t !== 'neck') continue;
      if (e.kind === 'squeeze') {
        const p = this.ctx.at(e.x, e.y, 0.3);
        this.ctx.flare(p, '#ff6a4a', 2.2, 12);
        this.ctx.shake(0.18);
        this.ctx.vfx.burst(p, { n: low ? 5 : 12, color: '#c86a5a', speed: 0.05, up: 0.05, size: 0.06, life: 14, gravity: 0.01, spread: 0.4 });
      } else if (e.kind === 'in') {
        // The in-breath: a sigh of dust off every fold as it rolls shut.
        for (const f of this.folds) this.ctx.vfx.burst(f.c.clone().setY(f.c.y + 0.3), { n: low ? 2 : 5, color: DUST, speed: 0.02, up: 0.02, size: 0.4, life: 20, smoke: true, spread: 0.6 });
      }
    }
  }

  /** Every redraw: the folds ease to where the door stands, the hide breathes, the seams warn. */
  draw(s: BattleState): void {
    const r = s.rules?.neck;
    const st = s.neck;
    const dt = Math.min(0.1, Math.max(0, this.now - this.last));
    this.last = this.now;
    const k = 1 - Math.exp(-dt * 7);
    this.breath += ((st?.open ?? true ? 1 : 0) - this.breath) * (1 - Math.exp(-dt * 2.5));
    // How soon the folds shut (seconds), on the out-breath.
    const cycle = r ? r.open + r.shut : 1;
    const phase = s.started && r ? s.waveT % cycle : 0;
    const soon = r && st?.open ? (r.open - phase) / 12 : 99;
    this.glow.begin();
    for (const f of this.folds) {
      const target = (f.door.bars.userData.target as number | undefined) ?? 1;
      f.open += (target - f.open) * k;
      // About to shut: the lips quiver and a red seam glows across the door.
      if (target > 0.5 && soon < 1.6) {
        f.open = Math.min(f.open, 0.97 + 0.03 * Math.sin(this.now * 40));
        const a = 1 - soon / 1.6;
        const y = this.ctx.ground(f.c.x, f.c.z) + 0.04;
        for (let n = -2; n <= 2; n++) this.glow.add(f.c.x + f.ax * n * 0.22, y, f.c.z + f.az * n * 0.22, SEAM, 0.45, 0.25 + 0.35 * a);
      }
    }
    this.glow.end();
    this.pose(this.now);
  }

  frame(now: number): void {
    this.now = now;
  }

  dispose(): void {
    for (const f of this.folds) {
      f.door.bars.visible = true;
      f.door.seal.visible = true;
    }
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.swell.dispose();
    this.lips.dispose();
  }
}
